import { foldTypographicDashes } from "@/lib/typographic-dash";

// 2026-04-22: single point for reading CMS-multilingual fields off a
// record (deal, voucher, activity, hotel, …). The backend schema added
// an `i18n` JSON column (WP2) that looks like:
//
//   { title: { nl: "Bremen …", en: "Bremen …", de: "Bremen …", fr: "Bremen …" }, … }
//
// but it's only populated once Jeanne runs the CMS auto-translate on
// each item. Until then we need to fall back to the legacy scalar
// column — `deal.title`, `voucher.name`, `activity.description`, etc.
//
// This helper centralises the fallback so individual call-sites don't
// each reinvent the chain.

// LAUNCH_READINESS_DE_BE.md P0 #15 (2026-05-19): fallback locale was
// "en" which caused .de voucher detail pages to render English copy
// when a German translation was missing — a worse user experience
// than falling back to Dutch (the source-of-truth language for
// Favotrip, originally a Dutch company). Changed to "nl". Pages that
// genuinely want an English fallback can pass { fallbackLocale: "en" }
// at the call site.
const DEFAULT_FALLBACK_LOCALE = "nl";
const LOCALIZED_VALUE_KEYS = ["nl", "de", "fr", "en", "pl"];

// Kartik #87 / #65 / #88 (2026-04-25): some i18n column values + some
// scalar columns store the locale's text as a *nested* multilang JSON
// blob (e.g. `i18n.title.nl = '{"en":"Harry...","nl":"Harry..."}'`)
// because the migration that populated the i18n column copied scalar
// JSON blobs into the locale-keyed slot instead of unwrapping them
// first. Detect and unwrap one extra level so the rendered UI never
// shows the raw `{"en":"..."}` wrapper.
/**
 * Find matching `}` for the leading `{` in `s`, ignoring `}` inside
 * JSON string literals. Returns 1-based length of the leading JSON
 * object, or -1 if unbalanced. Used to handle Kartik #88-update case
 * where a CMS clone-action appends ` (kopie)` *after* the JSON blob.
 */
function findLeadingJsonEnd(s) {
  if (s[0] !== "{") return -1;
  let depth = 0;
  let inString = false;
  let escape = false;
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (escape) {
      escape = false;
      continue;
    }
    if (ch === "\\") {
      escape = true;
      continue;
    }
    if (inString) {
      if (ch === '"') inString = false;
      continue;
    }
    if (ch === '"') {
      inString = true;
      continue;
    }
    if (ch === "{") depth++;
    else if (ch === "}") {
      depth--;
      if (depth === 0) return i + 1;
    }
  }
  return -1;
}

function unwrapNestedJsonBlob(str, locale, fallbackLocale, strict = false) {
  if (typeof str !== "string") return null;
  const trimmed = str.trim();
  if (!trimmed.startsWith("{")) return null;

  // Kartik #88-update (2026-04-25): some scalar values are
  // `{...JSON...} (kopie)` because the CMS clone-action appends
  // a suffix to the raw stored title. Extract leading JSON only
  // and re-attach the suffix to whichever locale resolves.
  const jsonEnd = findLeadingJsonEnd(trimmed);
  const candidate = jsonEnd > 0 ? trimmed.slice(0, jsonEnd) : trimmed;
  const suffix = jsonEnd > 0 ? trimmed.slice(jsonEnd) : "";

  try {
    const parsed = JSON.parse(candidate);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
    const hasKeys = LOCALIZED_VALUE_KEYS.some((k) =>
      Object.prototype.hasOwnProperty.call(parsed, k),
    );
    if (!hasKeys) return null;
    // We *did* identify a multilang shape — return what `pickLocalizedValue`
    // produces (which may be `""` when every locale is empty). That `""`
    // is preferable to leaking the raw wrapper, so we only fall back to
    // null if the picker itself returns a non-string sentinel.
    const nested = pickLocalizedValue(parsed, locale, fallbackLocale, undefined, !strict, strict);
    if (typeof nested === "string") {
      // Re-attach operator-added trailing decoration (e.g. " (kopie)")
      // verbatim so the CMS visual-cue isn't lost.
      return suffix ? (nested + suffix).trim() : nested;
    }
    // Strict: a multilang wrapper without the asked language is "empty",
    // never the raw wrapper (cleanString maps "" to null).
    return strict ? "" : null;
  } catch {
    return null;
  }
}

// `scan` (default true) enables the last-resort "first non-empty language"
// scan. With scan=false the picker is STRICT: it only looks at the market
// key, `locale` and `fallbackLocale`, and returns null (not "") when none
// of them carries a value, so the caller can try the scalar column before
// any other language is allowed to surface.
function pickLocalizedValue(value, locale, fallbackLocale, market, scan = true, strict = false) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const hasLocalizedKeys =
    LOCALIZED_VALUE_KEYS.some((key) =>
      Object.prototype.hasOwnProperty.call(value, key),
    ) ||
    // Fase 2: an object carrying only a market slot (e.g. `{ "nl-be": … }`
    // with no plain `nl`) still counts as localized WHEN that market is being
    // requested. Guarded by `market` so non-market callers are unchanged: for
    // them this term is always falsy and the key-set stays exactly as before.
    (Boolean(market) && Object.prototype.hasOwnProperty.call(value, market));
  if (!hasLocalizedKeys) return null;

  // Helper that returns a string only after stripping a possible
  // nested-JSON wrapper. Empty wrapper (e.g. all-locales-empty) yields
  // null so the caller falls through to the next candidate.
  const cleanString = (raw) => {
    if (typeof raw !== "string" || !raw.trim()) return null;
    const unwrapped = unwrapNestedJsonBlob(raw, locale, fallbackLocale, strict);
    if (unwrapped !== null) return unwrapped.trim() ? unwrapped : null;
    return raw;
  };

  // BE market layer (Fase 2, 2026-07-24): a market-specific override key
  // (e.g. `nl-be` / `fr-be`) wins over the plain language key so a Belgian
  // visitor gets Belgian copy, falling through to the language key when no
  // override exists. `market` is undefined for every non-market caller, so
  // this is a no-op for .nl / .de / .fr / .en / .pl.
  if (market) {
    const marketValue = cleanString(value[market]);
    if (marketValue) return marketValue;
  }

  const primary = cleanString(value[locale]);
  if (primary) return primary;

  const fallback = cleanString(value[fallbackLocale]);
  if (fallback) return fallback;

  if (!scan) return null;

  for (const [key, entry] of Object.entries(value)) {
    // Fase 2 leak-guard: a market slot (e.g. `nl-be` / `fr-be` — always
    // hyphenated) must never surface through this last-resort scan for a
    // caller that didn't ask for that exact market, or a present-but-empty
    // base locale (`{ nl: "", "nl-be": "…" }`) would leak Belgian copy to a
    // .nl/.de visitor, or the Dutch-Belgian slot to an fr-be visitor. Plain
    // language keys (nl/de/fr/en/pl) and any legacy key never contain a
    // hyphen, so pre-Fase-2 data is byte-for-byte unaffected.
    if (key !== market && key.includes("-")) continue;
    const cleaned = cleanString(entry);
    if (cleaned) return cleaned;
  }

  return "";
}

function readI18n(obj) {
  if (!obj || typeof obj !== "object") return null;
  const raw = obj.i18n;
  if (!raw) return null;
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (typeof raw === "object") return raw;
  return null;
}

/**
 * Returns the best available string for (obj, field, locale):
 *   1. obj.i18n[field][locale]                    — preferred
 *   2. obj.i18n[field][fallbackLocale] (default nl) — source-of-truth fallback
 *   3. obj[field]                                  — scalar column (Dutch source
 *                                                    text, counts as the fallback language)
 *   4. first non-empty language in obj.i18n[field] — only when there is no scalar
 *   5. obj[camelCase variant of field]             — last resort
 *   6. ""                                          — nothing available
 *
 * If the value is not a string (e.g. an object / null) we skip it and
 * fall through to the next step — the i18n column occasionally holds
 * nested objects for rich fields and we don't want to render `[object
 * Object]`.
 */
// INTL bug-batch 2026-06-10 item 1: the backend auto-translate pipeline
// occasionally echoes surrounding markup into the translated i18n value.
// Prod .fr showed category names like `<h1>escapades urbaines</h1>` and
// deal titles like `<strong>Bon Bien-être SpaWell</strong> | <em>…</em>`
// rendered as LITERAL text on tiles, voucher cards, checkout and in
// <title>. Title-like fields are plain text by design on every render
// surface, so strip inline HTML centrally for these field names. Rich
// fields (description, intro, package_info) keep their markup - those go
// through sanitizeHtml + dangerouslySetInnerHTML at the call sites.
//
// This is the render-side guard for both the already-polluted rows and
// any future polluted translation; cleaning the stored rows in the CMS
// i18n columns themselves is a backend/CMS follow-up.
const PLAIN_TEXT_FIELDS = new Set([
  "title",
  "name",
  "display_name",
  "displayName",
  "activity_name",
  "activityName",
]);

export function getLocalizedField(obj, field, locale, options = {}) {
  const value = resolveLocalizedField(obj, field, locale, options);
  if (typeof value !== "string") return value;
  const plainText =
    options.plainText !== undefined
      ? options.plainText
      : PLAIN_TEXT_FIELDS.has(field);
  if (!plainText) return value;
  // Always route plain-text fields through stripInlineHtml: besides stripping
  // stray inline markup it folds huisstijl dashes, so a dash-only title (no "<")
  // is normalised too (C5 no-em-dash content gate).
  return stripInlineHtml(value);
}

function resolveStrict(obj, field, locale, market) {
  const pick = (value) => pickLocalizedValue(value, locale, locale, market, false, true);
  const fromI18n = pick(readI18n(obj)?.[field]);
  if (fromI18n !== null) return fromI18n;
  const direct = obj[field];
  const fromObject = pick(direct);
  if (fromObject !== null) return fromObject;
  // A scalar multilang blob, optionally followed by " (kopie)". A plain
  // scalar string is the Dutch source text and never answers here.
  if (typeof direct === "string" && direct.trim().startsWith("{")) {
    const trimmed = direct.trim();
    const jsonEnd = findLeadingJsonEnd(trimmed);
    try {
      const parsed = JSON.parse(jsonEnd > 0 ? trimmed.slice(0, jsonEnd) : trimmed);
      const picked = pick(parsed);
      if (picked) return jsonEnd > 0 ? (picked + trimmed.slice(jsonEnd)).trim() : picked;
    } catch {
      /* not a multilang blob */
    }
  }
  return "";
}

function resolveLocalizedField(
  obj,
  field,
  locale,
  { fallbackLocale = DEFAULT_FALLBACK_LOCALE, market, strict = false } = {},
) {
  if (!obj || !field) return "";

  // `strict`: only the requested locale (and market) may answer. No plain
  // scalar (the Dutch source text), no any-language scan, no other field
  // variant: a missing or empty value in `locale` is "". For .de intros.
  if (strict) return resolveStrict(obj, field, locale, market);

  const i18n = readI18n(obj);
  const i18nMap = i18n?.[field];
  const directValue = obj[field];

  // 1. Strict i18n lookup: market key, locale, fallbackLocale (no scan).
  const localizedFromI18n = pickLocalizedValue(i18nMap, locale, fallbackLocale, market, false);
  if (localizedFromI18n !== null) {
    return localizedFromI18n;
  }

  // 2. A localized object in the scalar slot, strictly per locale/fallback.
  const localizedFromObject = pickLocalizedValue(directValue, locale, fallbackLocale, market, false);
  if (localizedFromObject !== null) {
    return localizedFromObject;
  }

  // 3. The scalar column is the Dutch source text and counts as the value of
  // the fallback language (nl). It wins over the any-language scan below, so a
  // record whose i18n only holds `pl` or `de` never shows that language to a
  // Dutch visitor while a Dutch source text exists.
  let blobParsed = null;
  let blobSuffix = "";
  if (typeof directValue === "string" && directValue.trim()) {
    const raw = directValue;
    // Kartik #84 (2026-04-22 18:12) + #88-update (2026-04-25): some
    // legacy scalar columns store the value as a multilang JSON blob,
    // optionally followed by an operator-added suffix like ` (kopie)`
    // (CMS clone-action artefact). Extract the leading `{...}`,
    // unwrap to the locale's string, and re-attach the suffix.
    const trimmed = raw.trim();
    if (trimmed.startsWith("{")) {
      const jsonEnd = findLeadingJsonEnd(trimmed);
      const candidate = jsonEnd > 0 ? trimmed.slice(0, jsonEnd) : trimmed;
      const suffix = jsonEnd > 0 ? trimmed.slice(jsonEnd) : "";
      try {
        const parsed = JSON.parse(candidate);
        const localizedFromJson = pickLocalizedValue(parsed, locale, fallbackLocale, market, false);
        if (localizedFromJson !== null) {
          return suffix
            ? (localizedFromJson + suffix).trim()
            : localizedFromJson;
        }
        // A multilang blob without locale/fallback value: only the scan
        // below can resolve it; never return the raw wrapper in that case.
        if (pickLocalizedValue(parsed, locale, fallbackLocale, market) !== null) {
          blobParsed = parsed;
          blobSuffix = suffix;
        }
      } catch {
        /* fall through to legacy scalar behaviour */
      }
    }
    if (!blobParsed) return raw;
  }

  // 4. Last resort: no Dutch/scalar text at all. Take the first non-empty
  // language from the i18n map, the scalar object, or the scalar JSON blob.
  const scannedI18n = pickLocalizedValue(i18nMap, locale, fallbackLocale, market);
  if (scannedI18n !== null) {
    return scannedI18n;
  }
  const scannedObject = pickLocalizedValue(directValue, locale, fallbackLocale, market);
  if (scannedObject !== null) {
    return scannedObject;
  }
  if (blobParsed) {
    const scanned = pickLocalizedValue(blobParsed, locale, fallbackLocale, market);
    return blobSuffix ? (scanned + blobSuffix).trim() : scanned;
  }

  // Snake → camel fallback so a caller passing `"activity_name"` still
  // finds `obj.activityName`, and vice-versa.
  const camel = field.replace(/_([a-z])/g, (_m, c) => c.toUpperCase());
  if (camel !== field && typeof obj[camel] === "string" && obj[camel].trim()) {
    return obj[camel];
  }
  const snake = field.replace(/[A-Z]/g, (m) => `_${m.toLowerCase()}`);
  if (snake !== field && typeof obj[snake] === "string" && obj[snake].trim()) {
    return obj[snake];
  }

  return "";
}

/**
 * INTL bug-batch 2026-06-10 item 9: true when the record carries an
 * actual translation for (field, locale) in its i18n column - i.e. the
 * value getLocalizedField returns is genuinely in `locale` and not the
 * NL fallback. Used by metadata builders that must NOT emit the Dutch
 * description on .de/.fr SERPs and instead generate a localized generic
 * description.
 */
export function hasLocalizedValue(obj, field, locale) {
  if (!obj || !field || !locale) return false;
  const i18n = readI18n(obj);
  const slot = i18n?.[field];
  if (!slot || typeof slot !== "object" || Array.isArray(slot)) return false;
  const raw = slot[locale];
  return typeof raw === "string" && raw.trim().length > 0;
}

/**
 * Gallery photo captions (deal.carousel_images[].caption) are set by the
 * deal-factory / CMS operator and may be EITHER a legacy flat string (one
 * language, historically Dutch) OR a localized { nl, de, en, fr } object.
 * Resolve to the visitor's locale with NL fallback, mirroring how
 * title/description resolve (getLocalizedField) so a localized page never
 * pairs a German title with a Dutch caption. A flat string is returned
 * verbatim so legacy rows keep rendering until the per-locale object is
 * populated. Unlike the i18n-column fields this reads the value directly
 * (captions live inside the carousel_images JSON, not in obj.i18n).
 */
export function resolveLocalizedCaption(
  caption,
  locale,
  { fallbackLocale = DEFAULT_FALLBACK_LOCALE } = {},
) {
  if (caption == null) return "";
  if (typeof caption === "string") return caption;
  const picked = pickLocalizedValue(caption, locale, fallbackLocale);
  return typeof picked === "string" ? picked : "";
}

/**
 * Kartik #5 (2026-04-25): voucher_promotion[*].description rows now carry
 * a `description_i18n: {en, nl, de, fr}` companion. Pick the localized
 * value for the current locale; fall back to legacy `description` so any
 * row that hasn't been translated yet still renders something readable.
 */
export function getPromotionDescription(item, locale, options = {}) {
  if (!item || typeof item !== "object") return "";
  const fallbackLocale = options.fallbackLocale || DEFAULT_FALLBACK_LOCALE;
  const i18n = item.description_i18n || item.descriptionI18n;
  if (i18n && typeof i18n === "object") {
    const picked = pickLocalizedValue(i18n, locale, fallbackLocale);
    if (picked) return stripInlineHtml(picked);
  }
  return typeof item.description === "string"
    ? stripInlineHtml(item.description)
    : "";
}

/**
 * TD-blocks 2026-07-02 (extended USPs): optional per-item subtitle for
 * voucher_promotion rows. New CMS fields `sub_description` (scalar) +
 * `sub_description_i18n` ({nl,de,fr,en,pl}) mirror the description /
 * description_i18n pair and resolve through the SAME pipeline
 * (pickLocalizedValue + stripInlineHtml), so a polluted translation can
 * never leak markup into the plain-text subtitle. Rows without the new
 * fields return "" and the caller renders icon + title only.
 */
export function getPromotionSubDescription(item, locale, options = {}) {
  if (!item || typeof item !== "object") return "";
  const fallbackLocale = options.fallbackLocale || DEFAULT_FALLBACK_LOCALE;
  const i18n = item.sub_description_i18n || item.subDescriptionI18n;
  if (i18n && typeof i18n === "object") {
    const picked = pickLocalizedValue(i18n, locale, fallbackLocale);
    if (picked) return stripInlineHtml(picked);
  }
  const raw = item.sub_description ?? item.subDescription;
  return typeof raw === "string" ? stripInlineHtml(raw) : "";
}

// Kartik #14 (2026-06-04): the voucher LISTING cards used to inline
// their own promotion-highlight extraction (reading description_i18n
// directly) and bypassed getPromotionDescription(), so stray inline
// <b>/</b> tags from the CMS leaked as literal text on the EN locale
// while the detail pages (which already use the helper) rendered clean.
// Centralise the listing extraction here so both surfaces strip
// identically.
export function extractPromotionHighlights(promotionItems, locale) {
  if (!Array.isArray(promotionItems)) return [];
  return promotionItems
    .map((item) => getPromotionDescription(item, locale))
    .map((s) => (typeof s === "string" ? s.trim() : ""))
    .filter(Boolean);
}

// Kartik #11 (2026-06-02): some CMS promotion rows carry stray inline
// formatting tags in a single locale slot (e.g. `description_i18n.en =
// "<b>Overnight stay…</b>"`, `description_i18n.fr = "<b>Choisissez…"`)
// while the sibling locales are plain. The USP block renders these as
// plain-text labels (`<span>{desc}</span>`), so React escapes the tags
// and the visitor literally sees `<b>…</b>`. These short labels carry no
// markup by design (plain text + icon), so strip any inline HTML tags
// and decode the handful of entities the CMS export emits.
const HTML_ENTITIES = {
  "&amp;": "&",
  "&lt;": "<",
  "&gt;": ">",
  "&quot;": '"',
  "&#39;": "'",
  "&nbsp;": " ",
};
function decodeEntities(value) {
  return value.replace(
    /&(?:amp|lt|gt|quot|nbsp|#39);/g,
    (m) => HTML_ENTITIES[m] || m,
  );
}
// Exported since 2026-07-02 (TD-blocks): VoucherFlexStrip +
// PartnerHotelsClient run the hotel-level cancellation_policy string
// through the exact same treatment as promotion descriptions before
// rendering it as plain text.
export function stripInlineHtml(value) {
  if (typeof value !== "string") return "";
  // Fold huisstijl dashes on every plain-text render path (deal/voucher titles,
  // activity + partner-hotel names) - these never pass through sanitizeHtml, so
  // a partner/CMS title like "Nausicaá - Europa's Grootste ..." would otherwise
  // reach <title>/<h1> verbatim and fail the C5 no-em-dash content gate.
  if (value.indexOf("<") === -1) {
    const decoded = value.indexOf("&") === -1 ? value : decodeEntities(value);
    return foldTypographicDashes(decoded);
  }
  const withoutTags = value.replace(/<\/?[a-z][^>]*>/gi, "");
  return foldTypographicDashes(decodeEntities(withoutTags).replace(/\s+/g, " ").trim());
}

// E2E QA 2026-06-10: CMS titles can arrive pre-encoded (e.g. "Geniet
// &amp; Herstel" or even double-encoded "&amp;#39;"). Plain-text render
// sites ({title} in JSX) then show the entity literally because React
// escapes the string again. Decode up to two passes (covers the
// double-encoded CMS export shape) and strip stray inline tags. Use this
// wherever a CMS title/name renders as PLAIN TEXT, never for HTML that
// goes through sanitizeHtml + dangerouslySetInnerHTML.
export function decodeDisplayText(value) {
  let out = stripInlineHtml(value);
  if (out.indexOf("&") !== -1) out = decodeEntities(out);
  return out;
}
